import React, { useEffect, useState } from 'react';
import { processStyleDefinition } from '../../util/styleProcessor';
import { styleProperties, styleDefaults, stylePropertiesForTheme } from './textBoxStyleProperties';
import { usedComponents } from '../../App/usedComponents';
import {
	findPropertyDefinitions,
	lazyStylePropertyLoadFunction,
	applyPreloadedStyleProperties,
} from '../util/lazyStylePropertyUtil';
import { propertiesDefinition } from './textBoxProperties';
import { StylePropertyDefinition } from '../../types/common';

const PREFIX = '.comp.compTextBox';
const NAME = 'TextBox';
export default function TextBoxStyle({
	theme,
}: Readonly<{ theme: Map<string, Map<string, string>> }>) {
	const [_, setReRender] = useState<number>(Date.now());

	if (globalThis.styleProperties[NAME] && !styleProperties.length && !styleDefaults.size)
		applyPreloadedStyleProperties(
			NAME,
			propertiesDefinition,
			styleProperties,
			stylePropertiesForTheme,
			styleDefaults,
		);

	useEffect(() => {
		const { designType, colorScheme } = findPropertyDefinitions(
			propertiesDefinition,
			'designType',
			'colorScheme',
		);
		const fn = lazyStylePropertyLoadFunction(
			NAME,
			(props, originalStyleProps) => {
				styleProperties.splice(0, 0, ...props);
				if (originalStyleProps) stylePropertiesForTheme.splice(0, 0, ...originalStyleProps);
				setReRender(Date.now());
			},
			styleDefaults,
			[designType, colorScheme],
		);

		if (usedComponents.used(NAME)) fn();
		usedComponents.register(NAME, fn);

		return () => usedComponents.deRegister(NAME);
	}, [setReRender]);
	const css =
		`
	${PREFIX} {
		display: flex;
		align-items: center;
	}

	${PREFIX} input {
		flex: 1;
		height: 100%;
		border: none;
		font: inherit;
		line-height: inherit;
		outline: none;
		padding: 0;
		background: transparent;
		color: inherit;
		min-width: 20px;
	}

	/* Edge draws its own reveal eye and clear cross inside password/text inputs, beside the
	   component's own show/hide icon (two eyes on BP sign-in, LZT-1646). The component owns them.
	   Separate rules: a browser that does not know one of these pseudo-elements drops the whole rule. */
	${PREFIX} input::-ms-reveal {
		display: none;
	}

	${PREFIX} input::-ms-clear {
		display: none;
	}

	${PREFIX}._isActive ._label,
	${PREFIX} ._label._noFloat {
		transform: translateY(-50%);
		bottom: 100%;
	}

	${PREFIX}._hasLeftIcon ._label {
		padding-left: 24px;
	}

	${PREFIX} ._label {
		position: absolute;
		user-select: none;
		pointer-events: none;
		bottom: 50%;
		transform: translateY(50%);
		transition: transform 0.2s ease-in-out, left 0.2s ease-in-out, bottom 0.2s ease-in-out;
	}

	${PREFIX} ._rightIcon,
	${PREFIX} ._leftIcon {
		width: 24px;
	}

	${PREFIX}._bigDesign1 ._leftIcon {
		margin-right: 10px;
		border-right: 1px solid;
	}

	${PREFIX}._bigDesign1 ._label {
    	margin-top: 0px;
	}

	${PREFIX}._bigDesign1._hasLeftIcon ._label {
		padding-left: 36px;
	}

	${PREFIX}._bigDesign1._hasValue ._label,
	${PREFIX}._bigDesign1._isActive ._label,
	${PREFIX}._bigDesign1 ._label._noFloat {
		margin-top: -30px;
		bottom: auto;
		transform: none;
	}

	${PREFIX}._bigDesign1 ._inputBox {
		padding-top: 10px;
	}

	${PREFIX} ._rightIcon {
		padding: 0px 5px;
	}

	${PREFIX} ._label._float {
		bottom: 0px;
	}

	${PREFIX} ._clearText, ${PREFIX} ._passwordIcon {
		cursor: pointer;
	}

	${PREFIX} ._supportText {
		position:absolute;
		z-index:1;
		left: 0;
		top: 100%;
		margin-top: 5px;
	}

	${PREFIX} ._controlButtons {
		display: none;
		margin-right: 5px;
	}

	${PREFIX}:hover ._controlButtons,
	${PREFIX}._editMode ._controlButtons {
		display: flex;
	}

	` +
		processStyleDefinition(PREFIX, styleProperties, styleDefaults, theme) +
		processStyleDefinition(
			PREFIX,
			passwordIconInErrorProperties(styleProperties),
			styleDefaults,
			theme,
		);

	return <style id="TextBoxCss">{css}</style>;
}

// The error state recolours every right icon (`._hasError ._rightIcon`; the mono theme makes it
// transparent), which also hid the password eye while the field showed an error (CXZ-1305). The
// eye keeps its rest, active and value colour: the same rules again for the eye in the error
// state, one class stronger than the error ones.
const NORMAL_RIGHT_ICON_COLOR = /^textBox(Active|Value)?RightIconColor/;

function passwordIconInErrorProperties(
	props: Array<StylePropertyDefinition>,
): Array<StylePropertyDefinition> {
	return props
		.filter(p => p.sel && NORMAL_RIGHT_ICON_COLOR.test(p.n))
		.map(p => ({
			...p,
			sel: p
				.sel!.split(',')
				.map(s =>
					s.trim().replace(/ \._rightIcon$/, '._hasError ._passwordIcon._rightIcon'),
				)
				.join(', '),
		}));
}
